import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { MapOutlined as MapOutlinedIcon } from "@mui/icons-material";
import AddressbookPanelShell from "../../addressbook/panels/AddressbookPanelShell";

const LEAFLET_CSS_ID = "leaflet-css";
const LEAFLET_JS_ID = "leaflet-js";

const ensureLeaflet = async () => {
  if (window.L) return window.L;

  if (!document.getElementById(LEAFLET_CSS_ID)) {
    const css = document.createElement("link");
    css.id = LEAFLET_CSS_ID;
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);
  }

  if (document.getElementById(LEAFLET_JS_ID)) {
    return new Promise((resolve, reject) => {
      const wait = () => {
        if (window.L) resolve(window.L);
        else setTimeout(wait, 50);
      };
      wait();
      setTimeout(() => {
        if (!window.L) reject(new Error("Leaflet failed to load"));
      }, 10000);
    });
  }

  await new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = LEAFLET_JS_ID;
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });

  if (!window.L) throw new Error("Leaflet failed to load");
  return window.L;
};

const getAddressString = (property) => {
  const address = property?.identity?.address || {};
  const line1 = [address.streetNo, address.streetName, address.unit]
    .filter((v) => String(v || "").trim())
    .join(" ")
    .trim();
  const line2 = [address.city, address.state, address.zip]
    .filter((v) => String(v || "").trim())
    .join(", ")
    .trim();
  return [line1, line2].filter(Boolean).join(", ").trim();
};

const getLatLng = (property) => {
  // Canonical: presenter-normalized top-level `geolocation`.
  const geo = property?.geolocation;
  const lat = Number(geo?.latitude);
  const lng = Number(geo?.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  return null;
};

const geocodeAddress = async (address) => {
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) return null;
  const rows = await res.json();
  const first = Array.isArray(rows) ? rows[0] : null;
  if (!first) return null;
  const lat = Number(first.lat);
  const lng = Number(first.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const buildMarkerHtml = (num) => `
  <div style="
    width: 26px;
    height: 26px;
    border-radius: 999px;
    background: #1D4ED8;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font: 700 12px/1 sans-serif;
    border: 2px solid #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.35);
  ">${num}</div>
`;

const PropertyMapPanel = ({ selectedProperties = [], fallbackProperty = null, onClose }) => {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [plottedCount, setPlottedCount] = useState(0);

  const propertiesToMap = useMemo(() => {
    if (Array.isArray(selectedProperties) && selectedProperties.length > 0) {
      return selectedProperties;
    }
    return fallbackProperty ? [fallbackProperty] : [];
  }, [selectedProperties, fallbackProperty]);

  useEffect(() => {
    let cancelled = false;

    const renderMap = async () => {
      if (!mapElRef.current) return;
      setLoading(true);

      try {
        const L = await ensureLeaflet();
        if (cancelled) return;

        if (!mapRef.current) {
          mapRef.current = L.map(mapElRef.current, { zoomControl: true });
          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 19,
            attribution: "&copy; OpenStreetMap contributors",
          }).addTo(mapRef.current);
        }

        if (layerRef.current) {
          layerRef.current.clearLayers();
        } else {
          layerRef.current = L.layerGroup().addTo(mapRef.current);
        }

        const bounds = L.latLngBounds([]);
        const geocodeCache = new Map();
        let plotted = 0;
        let lastPoint = null;

        for (let i = 0; i < propertiesToMap.length; i += 1) {
          const property = propertiesToMap[i];
          const number = i + 1;
          const title = property?.name || getAddressString(property) || `Property ${number}`;
          const address = getAddressString(property) || "Address unavailable";

          let point = getLatLng(property);
          if (!point && address && address !== "Address unavailable") {
            if (!geocodeCache.has(address)) {
              geocodeCache.set(address, await geocodeAddress(address));
            }
            point = geocodeCache.get(address);
          }

          if (!point) continue;

          const icon = L.divIcon({
            html: buildMarkerHtml(number),
            className: "property-marker",
            iconSize: [26, 26],
            iconAnchor: [13, 13],
            popupAnchor: [0, -14],
          });

          const marker = L.marker([point.lat, point.lng], { icon }).addTo(layerRef.current);
          const popup = `
            <div style="min-width: 180px; font: 12px/1.4 sans-serif; color: #0f172a;">
              <div style="font-weight: 700; margin-bottom: 4px;">#${number} ${title}</div>
              <div>${address}</div>
            </div>
          `;

          marker.bindPopup(popup);
          marker.bindTooltip(`#${number} ${title}`, { direction: "top", offset: [0, -10] });
          marker.on("mouseover", () => marker.openTooltip());
          marker.on("click", () => marker.openPopup());

          bounds.extend([point.lat, point.lng]);
          plotted += 1;
          lastPoint = point;
        }

        if (cancelled) return;
        setPlottedCount(plotted);

        if (plotted === 1 && lastPoint) {
          // Single pin: fitBounds on a zero-area bounds jumps to max zoom.
          // Center on it with a sensible neighborhood-level zoom instead.
          mapRef.current.setView([lastPoint.lat, lastPoint.lng], 15);
        } else if (bounds.isValid()) {
          mapRef.current.fitBounds(bounds.pad(0.2));
        } else {
          // No property has coordinates: show the whole US as a neutral state.
          mapRef.current.setView([39.8283, -98.5795], 4);
        }
      } catch (e) {
        console.error("Failed to render property map:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    renderMap();

    return () => {
      cancelled = true;
    };
  }, [propertiesToMap]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        layerRef.current = null;
      }
    };
  }, []);

  if (!propertiesToMap.length) {
    return (
      <AddressbookPanelShell
        onClose={onClose}
        titleContent={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <MapOutlinedIcon sx={{ fontSize: 16, color: "#1D4ED8" }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Map</Typography>
          </Box>
        }
      >
        <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>
          <Typography sx={{ fontSize: 14 }}>Select a property (or check multiple properties) to view map.</Typography>
        </Box>
      </AddressbookPanelShell>
    );
  }

  return (
    <AddressbookPanelShell
      onClose={onClose}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <MapOutlinedIcon sx={{ fontSize: 16, color: "#1D4ED8" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            Map ({propertiesToMap.length})
          </Typography>
        </Box>
      }
    >
      <Box sx={{ position: "relative", height: "100%", minHeight: 0 }}>
        <Box ref={mapElRef} sx={{ position: "absolute", inset: 0 }} />
        {!loading && plottedCount === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: "50%",
              transform: "translateX(-50%)",
              px: 2,
              py: 1,
              borderRadius: 1,
              backgroundColor: "rgba(255,255,255,0.92)",
              border: "1px solid #E2E8F0",
              boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
              color: "#334155",
              fontSize: 13,
              zIndex: 500,
              maxWidth: "90%",
              textAlign: "center",
            }}
          >
            {propertiesToMap.length === 1
              ? "No location available for this property."
              : `No location available for the selected ${propertiesToMap.length} properties.`}
          </Box>
        )}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
              color: "#334155",
              fontSize: 13,
            }}
          >
            Loading map...
          </Box>
        )}
      </Box>
    </AddressbookPanelShell>
  );
};

export default PropertyMapPanel;
